import { useState } from 'react'
import { Box, Button, Grid, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useSelector } from 'react-redux'
import "@fontsource/rubik";
import File from '../../assets/file.svg'
import CustomTooltip from '../CustomTooltip'
import CustomVideoLinearProgress from '../CustomVideoLinearProgress'
import useCustomDropzone from '../../hooks/useCustomDropzone'
import useCustomTheme from '../../theme/useCustomTheme'
import { uploadCategory } from '../../constants/constants'
import { REVISION_CONTENT_ACCEPT, REVISION_CONTENT_EXTENSIONS, fileExtension, isSupportedRevisionContent } from '../../utils/revisionContent'

const TYPES_TOOLTIP = Object.keys(REVISION_CONTENT_EXTENSIONS)
  .map((type) => `${type}: ${REVISION_CONTENT_EXTENSIONS[type].join(' ')}`)
  .join('\n');

/**
 * The "add files" half of the Files tab: a dropzone for the open revision
 * request, with whatever is currently uploading listed underneath it.
 *
 * The picker is filtered to the extensions the server accepts, because the
 * alternative is a round trip that ends in 422 `unsupported_type` - the file is
 * refused either way, and refusing it here costs the user nothing.
 *
 * Uploads themselves are run by the shared upload hook (`useCustomDropzone`
 * feeds it), so a file keeps uploading when the tab is switched back to notes
 * or the page is scrolled away. What finishes is announced through redux and
 * the list beside this box reloads itself.
 */
const RevisionContentUploadBox = (props) => {
  const {
    uploadProps,
    revisionId,
    eventId,
    isGuest = false,
    guestUserId,
    collabCode,
    disabled = false,
    disabledReason,
  } = props;

  const [theme] = useCustomTheme();
  const uploadReducer = useSelector((state) => state.root.uploadReducer);
  // Files the picker refused. Without this the drop is a no-op and the user is
  // left wondering whether anything happened.
  const [rejected, setRejected] = useState([]);

  const {
    selectedFiles, setSelectedFiles,
    uploadStatus, setUploadStatus,
    setProgress, setRequestController,
  } = uploadProps || {};

  // Without a revision there is nothing to attach a file to: pre_flight would
  // answer 404 "Revision not found!!".
  const isDisabled = disabled || !revisionId || !uploadProps;

  const { getInputProps, getRootProps, style, open } = useCustomDropzone({
    files: selectedFiles || [],
    setFiles: setSelectedFiles,
    uploadStatus,
    setUploadStatus,
    setProgress,
    setRequestController,
    isDisabled,
    accept: REVISION_CONTENT_ACCEPT,
    category: uploadCategory.revisionContent,
    // `revision_id` selects the revision and the project comes from it. An
    // `event_id` that disagrees with the revision's project is a 404, so the
    // project is sent only where it is known to be the revision's own.
    extraPayload: {
      revision_id: revisionId,
      ...(eventId ? { event_id: eventId } : {}),
      ...(isGuest ? { guest_user_id: guestUserId, collab_code: collabCode } : {}),
    },
    isGuest,
    onDropRejected: (fileRejections) => setRejected(
      (fileRejections || []).map((rejection) => rejection?.file?.name).filter(Boolean)
    ),
  });

  // Rows for this revision only - the drawer shows everything, this box shows
  // what the user just added here.
  const rejectionReason = (name) => (isSupportedRevisionContent(name)
    ? 'could not be added'
    : fileExtension(name)
      ? `is not a file type your editor can use (${fileExtension(name)})`
      : 'has no file extension, so its type cannot be read');

  const inFlight = (Array.isArray(uploadReducer?.selected_files) ? uploadReducer.selected_files : [])
    .map((file, index) => ({ file, index }))
    .filter(({ file }) => file?.category === uploadCategory.revisionContent
      && (!revisionId || !file?.extraPayload?.revision_id || file.extraPayload.revision_id === revisionId));

  return (
    <Box data-testid='revision-content-upload-box' sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Typography
        sx={{ fontWeight: 600, fontFamily: 'Rubik', color: 'black', mb: 1, fontSize: { xs: '10px', md: '12px', lg: '13px', xl: '18px' } }}
      >
        Add files for your editor
      </Typography>

      {/* The target fills whatever the column has left rather than sitting as a
          strip at the top of it: a drop target is aimed at, and a bigger one is
          easier to hit. The in-flight rows below are capped instead, so a busy
          upload trims the box rather than squashing it away. */}
      <Grid
        {...getRootProps({
          style: {
            ...style,
            padding: '1em',
            flexGrow: 1,
            justifyContent: 'center',
            opacity: isDisabled ? 0.55 : 1,
          },
          className: 'dropzone',
        })}
        data-testid='revision-content-dropzone'
        sx={{ minHeight: { xs: '92px', md: '120px', lg: '150px', xl: '210px' } }}
      >
        <input {...getInputProps()} />
        <Box className='flex col a-center j-center' gap={{ xs: 1, xl: 2 }} sx={{ width: '100%', height: '100%' }}>
          <Box sx={{ width: { xs: '24px', md: '34px', lg: '44px', xl: '64px' }, height: { xs: '20px', md: '28px', lg: '36px', xl: '50px' } }}>
            <img src={File} height='100%' width='100%' alt='' />
          </Box>

          <Typography
            sx={{ fontWeight: 600, textAlign: 'center', letterSpacing: 1.1, color: '#878887', fontFamily: 'Rubik', fontSize: { xs: '8px', sm: '9px', md: '10px', lg: '13px', xl: '19px' } }}
          >
            Drag &amp; Drop your files here
          </Typography>

          <Button
            color='secondary'
            variant='contained'
            disabled={isDisabled}
            onClick={() => open()}
            data-testid='revision-content-browse'
            style={{
              fontFamily: 'Rubik',
              backgroundColor: isDisabled ? '#52636C50' : theme.palette.secondary.main,
              borderRadius: '5px', border: 'none', cursor: 'pointer', fontWeight: 600,
              textTransform: 'capitalize', minWidth: '70px',
            }}
            sx={{ fontSize: { xs: '7px', sm: '8px', md: '9px', lg: '11px', xl: '15px' }, paddingY: { xs: 0.3, lg: 0.4 }, paddingX: { xs: 1, xl: 2 } }}
          >
            Or Browse
          </Button>
        </Box>
      </Grid>

      <Box className='flex row j-end a-center' gap={0.5} sx={{ flexShrink: 0, mt: 0.5 }}>
        <Typography
          textAlign='end'
          sx={{ fontFamily: 'Rubik', fontWeight: 600, fontSize: { xs: '7px', md: '8px', lg: '9px', xl: '13px' } }}
        >
          Video, image, audio and documents
        </Typography>
        <CustomTooltip title={<span style={{ whiteSpace: 'pre-line' }}>{TYPES_TOOLTIP}</span>}>
          <InfoOutlinedIcon sx={{ fontSize: { xs: '8px !important', md: '9px !important', lg: '10px !important', xl: '15px !important' }, color: '#000000' }} />
        </CustomTooltip>
      </Box>

      {/* Why the box is inert, rather than leaving the user to guess. */}
      {isDisabled && !!disabledReason && (
        <Typography
          data-testid='revision-content-upload-disabled'
          sx={{ fontFamily: 'Rubik', fontStyle: 'italic', color: '#08231B', opacity: 0.75, mt: 0.8, fontSize: { xs: '7px', md: '8px', lg: '9px', xl: '12px' } }}
        >
          {disabledReason}
        </Typography>
      )}

      {rejected.length > 0 && (
        <Box
          data-testid='revision-content-rejected'
          onClick={() => setRejected([])}
          sx={{ flexShrink: 0, mt: 0.8, cursor: 'pointer' }}
        >
          {rejected.map((name) => (
            <Typography
              key={name}
              sx={{ fontFamily: 'Rubik', color: '#CE0101', fontWeight: 600, wordBreak: 'break-word', fontSize: { xs: '7px', md: '8px', lg: '9px', xl: '12px' } }}
            >
              {`“${name}” ${rejectionReason(name)}.`}
            </Typography>
          ))}
        </Box>
      )}

      {inFlight.length > 0 && (
        <Box data-testid='revision-content-inflight' sx={{ flexShrink: 0, maxHeight: '38%', overflowY: 'auto', mt: 1 }}>
          {inFlight.map(({ file, index }) => (
            <Box
              key={`${file?.name}-${index}`}
              className='flex a-center'
              gap={1}
              sx={{ borderBottom: '1px solid #0000000f', paddingY: 0.5 }}
            >
              <CustomTooltip title={file?.name}>
                <Typography
                  sx={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'Rubik', fontSize: { xs: '7px', md: '8px', lg: '9px', xl: '13px' } }}
                >
                  {file?.name}
                </Typography>
              </CustomTooltip>
              <Box sx={{ width: '50%', display: 'flex', justifyContent: 'flex-end' }}>
                <CustomVideoLinearProgress
                  drawerUpload
                  progress={uploadReducer?.progress?.[index]}
                  status={uploadReducer?.upload_status?.[index]}
                  fontSize={11}
                  bar={4}
                />
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default RevisionContentUploadBox
